import React, { lazy, Suspense, useEffect } from "react"
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom"
import Box from "@mui/material/Box"
import { ThemeProvider } from "@mui/material/styles"
import { baseTheme } from "./theme"
import ErrorBoundary from "./shared/ErrorBoundary"
import ProtectedRoute from "./features/auth/ProtectedRoute"
import SshGate from "./features/auth/SshGate"
import { TerminalProvider } from "./features/terminal/TerminalContext"
import { ResearchProvider } from "./features/deep-research/ResearchContext"
import { ChatProvider, useChatContext } from "./features/chat/ChatContext"
import { AgentChatProvider } from "./features/chat/AgentChatContext"
import { AuthProvider } from "./features/auth/AuthContext"
import { useAuth } from "./features/auth/AuthContext"
import { SpotlightProvider, useSpotlight } from "./features/spotlight/SpotlightContext"
import { ChillAudioProvider } from "./features/chill/ChillAudioContext"
import { HolosProductivityProvider } from "./features/desktop/HolosProductivityContext"
import { DesktopWorkspaceProvider } from "./features/desktop/useDesktopWorkspace"
import { DeployEventProvider } from "./features/deploy/DeployEventContext"
import { AppearanceProvider } from "./features/appearance/AppearanceContext"
import TerminalWindow from "./features/terminal/TerminalWindow"
import LiveChat from "./features/live-chat/LiveChat"
import SpotlightSearch from "./features/spotlight/SpotlightSearch"
import FloatingWindow from "./features/spotlight/FloatingWindow"
import DeployBanner from "./features/deploy/DeployBanner"
import DeployConfirmDialog from "./features/deploy/DeployConfirmDialog"

const HomeDashboard = lazy(() => import("./features/home/HomeDashboard"))
const JarvisPage = lazy(() => import("./features/chat/JarvisPage"))
const FridayPage = lazy(() => import("./features/chat/FridayPage"))
const VaultPage = lazy(() => import("./features/vault/VaultPage"))
const SettingsPage = lazy(() => import("./features/settings/SettingsPage"))
const ServerPage = lazy(() => import("./features/stats/ServerPage"))
const DatabasePage = lazy(() => import("./features/database/DatabasePage"))
const DeepResearchPage = lazy(() => import("./features/deep-research/DeepResearchPage"))
const LoginPage = lazy(() => import("./features/auth/LoginPage"))
const HutGamePage = lazy(() => import("./features/hut-frame/HutGamePage"))
const MailPage = lazy(() => import("./features/mail/MailPage"))
const IDEPage = lazy(() => import("./features/ide/IDEPage"))
const ChillPage = lazy(() => import("./features/chill/ChillPage"))
const ReminderPage = lazy(() => import("./features/reminder/ReminderPage"))
const WorkspacePage = lazy(() => import("./features/workspace/WorkspacePage"))
const DesktopPage = lazy(() => import("./features/desktop/DesktopPage"))
const SyncPage = lazy(() => import("./features/sync/SyncPage"))

function PageLoader() {
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--app-bg)",
      }}
    />
  )
}

function StartupRedirect() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (location.pathname !== "/") return
    const target = localStorage.getItem("startup_page") ?? "dashboard"
    if (target !== "dashboard") {
      navigate(`/${target}`, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return null
}

function FloatingWindows() {
  const { visibleWindows } = useSpotlight()
  return visibleWindows.map((win) => <FloatingWindow key={win.id} win={win} />)
}

function GlobalOverlays() {
  const { user } = useAuth()
  const { chatWindowHost, chatWindowVisible } = useChatContext()
  if (!user) return null
  // Dashboard overlays (terminal, messenger, spotlight, deploy) belong to the
  // full app — SSH-gated users only get Hut & Heroes, so render nothing for them.
  if (!user.ssh_linked) return null
  return (
    <>
      <DeployBanner />
      <DeployConfirmDialog />
      <TerminalWindow />
      <LiveChat windowHost={chatWindowHost} windowVisible={chatWindowVisible} />
      <SpotlightSearch />
      <FloatingWindows />
    </>
  )
}

export default function App() {
  return (
    <ThemeProvider theme={baseTheme}>
      <ErrorBoundary>
        <AuthProvider>
          <DesktopWorkspaceProvider>
          <AppearanceProvider>
          <DeployEventProvider>
          <TerminalProvider>
            <SpotlightProvider>
            <HolosProductivityProvider>
            <ChillAudioProvider>
            <ResearchProvider>
              <ChatProvider>
              <AgentChatProvider>
                <BrowserRouter>
                  <StartupRedirect />
                  <GlobalOverlays />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/" element={<ProtectedRoute><SshGate><HomeDashboard /></SshGate></ProtectedRoute>} />
                      <Route path="/desktop" element={<ProtectedRoute><SshGate><ErrorBoundary><DesktopPage /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/jarvis" element={<ProtectedRoute><SshGate><JarvisPage /></SshGate></ProtectedRoute>} />
                      <Route path="/friday" element={<ProtectedRoute><SshGate><FridayPage /></SshGate></ProtectedRoute>} />
                      <Route path="/vault" element={<ProtectedRoute><SshGate><VaultPage /></SshGate></ProtectedRoute>} />
                      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                      <Route path="/stats" element={<ProtectedRoute><SshGate><ErrorBoundary><ServerPage /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/database" element={<ProtectedRoute><SshGate><ErrorBoundary><DatabasePage /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/research" element={<ProtectedRoute><SshGate><ErrorBoundary><DeepResearchPage /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/hut" element={<ProtectedRoute><ErrorBoundary><HutGamePage /></ErrorBoundary></ProtectedRoute>} />
                      <Route path="/mail" element={<ProtectedRoute><ErrorBoundary><MailPage /></ErrorBoundary></ProtectedRoute>} />
                      <Route path="/ide" element={<ProtectedRoute><ErrorBoundary><IDEPage /></ErrorBoundary></ProtectedRoute>} />
                      <Route path="/reminder" element={<ProtectedRoute><ErrorBoundary><ReminderPage /></ErrorBoundary></ProtectedRoute>} />
                      <Route path="/sync" element={<ProtectedRoute><SshGate><ErrorBoundary><SyncPage /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/chill" element={<ProtectedRoute><SshGate><ErrorBoundary><ChillPage /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/assistants" element={<ProtectedRoute><SshGate><ErrorBoundary><WorkspacePage id="assistants" /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/knowledge" element={<ProtectedRoute><SshGate><ErrorBoundary><WorkspacePage id="knowledge" /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/dev" element={<ProtectedRoute><SshGate><ErrorBoundary><WorkspacePage id="dev" /></ErrorBoundary></SshGate></ProtectedRoute>} />
                      <Route path="/analysis" element={<Navigate to="/stats?tab=analysis" replace />} />
                      <Route path="/programmer-hut" element={<Navigate to="/hut" replace />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </AgentChatProvider>
              </ChatProvider>
            </ResearchProvider>
            </ChillAudioProvider>
            </HolosProductivityProvider>
            </SpotlightProvider>
          </TerminalProvider>
          </DeployEventProvider>
          </AppearanceProvider>
          </DesktopWorkspaceProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  )
}
